/**
 * Design tokens — fonte única da verdade.
 *
 * Todo valor visual é referenciado como CSS custom property COM fallback, para
 * que a aplicação continue correta mesmo sem o `style.css` da marca carregado.
 * Componentes importam daqui em vez de escrever hex literal.
 *
 * Ao introduzir ou renomear um token compartilhado, atualize também o
 * `style.scss` das marcas e `docs/whitelabel/settings-schema.md`.
 */

export const colors = {
  primary: 'var(--color-primary, #02EA75)',
  primaryDark: 'var(--color-primary-dark, #02C864)',
  brandHeader: 'var(--color-brand-header, #02EA75)',

  bgPage: 'var(--color-bg-page, #F9FAFB)',
  surface: 'var(--color-surface, #FFFFFF)',
  surfaceMuted: 'var(--color-surface-muted, #F3F4F6)',

  textPrimary: 'var(--color-text-primary, #111827)',
  textSecondary: 'var(--color-text-secondary, #6B7280)',
  textMuted: 'var(--color-text-muted, #9CA3AF)',

  border: 'var(--color-border, #E5E7EB)',
  borderSoft: 'var(--color-border-soft, #F3F4F6)',

  success: 'var(--color-success, #16A34A)',
  successSoft: 'var(--color-success-soft, #DCFCE7)',
  error: 'var(--color-error, #DC2626)',
  warning: 'var(--color-warning, #F59E0B)',

  white: '#FFFFFF',
} as const;

export const radius = {
  sm: 'var(--radius-sm, 8px)',
  md: 'var(--radius-md, 12px)',
  lg: 'var(--radius-lg, 16px)',
  xl: 'var(--radius-xl, 24px)',
  full: 'var(--radius-full, 9999px)',
} as const;

export const fontFamily =
  "var(--font-family, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)";

/**
 * Recuo lateral das barras de largura total, por breakpoint (16/24/32px). O
 * cabeçalho da marca e a barra do seletor de idioma usam o mesmo valor, então
 * nas telas até 1280px o logo, o botão do cabeçalho e a bandeira ficam na mesma
 * linha vertical.
 */
export const pageGutterX = { xs: 2, sm: 3, lg: 4 };

/** Faixa de conteúdo do cabeçalho da marca: centralizada, no máximo 1280px. */
export const contentContainerSx = {
  width: '100%',
  maxWidth: 1280,
  mx: 'auto',
  px: pageGutterX,
  boxSizing: 'border-box' as const,
};

/** Botão primário da marca — reaproveitado em todas as telas. */
export const primaryButtonSx = {
  backgroundColor: colors.primary,
  color: colors.white,
  fontWeight: 700,
  textTransform: 'none' as const,
  borderRadius: radius.sm,
  boxShadow: 'none',
  '&:hover': { backgroundColor: colors.primaryDark, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: colors.primary, opacity: 0.6, color: colors.white },
};

/** Cartão branco com borda suave, usado nos planos e no resumo do pedido. */
export const cardSx = {
  backgroundColor: colors.surface,
  border: `1px solid ${colors.borderSoft}`,
  borderRadius: radius.lg,
};
